import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { COMMON_TYPE } from '@/types/enums';
import { ConfirmEmailPostRequest } from '@/types/http-payload/auth';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import SendEmailSuccessIcon from '@/assets/icons/send-email-success-icon.svg';
import BackToSigninBtn from '@/components/general/BackToSigninBtn';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
export default observer(function ForgetPasswordPage() {
    // hooks
    const { t } = useTranslation();
    const { pathname } = useLocation();

    // store
    const {
        authStore: { confirmEmailForgetPassword }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {
            email: ''
        }
    });

    const [isSendMailSuccess, setIsSendMailSuccess] = useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        const dataSubmit: ConfirmEmailPostRequest = {
            email: data.email,
            type: isPathEk(pathname) ? COMMON_TYPE.EK : COMMON_TYPE.GAS
        };
        const res = await flowResult(confirmEmailForgetPassword(dataSubmit));
        if (res) {
            setIsSendMailSuccess(true);
        }
    };

    return isSendMailSuccess ?
        (
            <div className='grid gap-8'>
                <div className='flex justify-center'>
                    <img src={SendEmailSuccessIcon} alt='' className='w-17' />
                </div>
                <div className='text-center'>
                    ご入力いただいたメールアドレス宛にメールを送信いたしました。
                    <br />
                    メール内に記載されたURLをクリックして、パスワードのリセットを行って下さい。
                </div>
                <BackToSigninBtn />
            </div>
        ) :
        (
            <div>
                <h3 className='title-1 text-center'>
                    {t('words_title.forgot_password')}
                </h3>

                <div className='pt-8 pb-2 text-fs-mid14 text-center'>
                    登録済みのメールアドレスを入力して下さい。
                    <br />
                    パスワードリセット用のURLをメールでお送りいたします。
                </div>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <FormField
                            control={form.control}
                            name='email'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('field.email')}
                                            isHalfWidth
                                            {...field}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                form.clearErrors('email');
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='flex justify-center'>
                            <AnimateButton
                                contentComp={t('button.send')}
                                parentClassName='mt-8 h-[48px]'
                                disabled={!form.watch('email')}
                                type='submit'
                            >
                            </AnimateButton>
                        </div>
                    </form>
                </Form>
            </div>
        );
});
